// @ts-strict-ignore
import { Component, computed, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import * as C from "../../../../../../shared/constants/globals.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";
import { ProgressCheck } from "src/app/shared/models/progress-check.model";
import { KeyDerivationsService } from "@ripsim/services/lib/services/key-derivations.service";
import { UtimacoKeySpecifier } from "../../../../../../api/models/RipSim/Utimaco/Contracts/utimac-specifier";
import { KeyDerivationEntity } from "../../../../../../api/models/key-derivation-entity";
import { EWIZ_DERIVED_KEYS_VISIBLE_TOKEN } from "src/app/shared/constants/feature-flags.constants";

@Component({
  selector: C.EWIZ_OTA_KEYS_CONFIGURATION,
  templateUrl: "./ota-keys-configuration.component.html",
  standalone: false
})
export class EWizOTAKeysConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck, ProgressCheck {
  private readonly keyDerivations = signal<KeyDerivationEntity[]>([]);

  constructor(
    private dataService: DataService,
    private keyDerivationService: KeyDerivationsService,
    private destroyRef: DestroyRef
  ) {
    super();
  }

  public scp80KeyOptions = computed(() =>
    this.keyDerivations().filter((key) => key.keyRef.specifier === UtimacoKeySpecifier.MasterKeySmsOtaDerivation)
  );
  public scp81KeyOptions = computed(() =>
    this.keyDerivations().filter((key) => key.keyRef.specifier === UtimacoKeySpecifier.MasterKeyHttpOtaDerivationHkdf)
  );
  /**
   * Gates every derived-key control in the template: the "Derived" generation option and the
   * key-selection dropdown it reveals. Off while `EWIZ_DERIVED_KEYS_VISIBLE` is false (RSS-1685).
   */
  public readonly isDerivationConfigActive = inject(EWIZ_DERIVED_KEYS_VISIBLE_TOKEN);

  ngOnInit() {
    this.profileValues.otaKeysVisited = true;
    this.loadKeyDerivations();
    this.handleFormEvents();

    this.errorCheck(this.profileValues, this.dataService);
  }

  private loadKeyDerivations(): void {
    // Nothing renders the derivations while the feature is hidden, so do not ask for them.
    if (!this.isDerivationConfigActive) {
      return;
    }

    this.keyDerivationService
      .getKeyDerivations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.keyDerivations.set(
            (response.results ?? []).map((key) => ({
              ...key,
              keyIndex: key.keyIndex ?? 0,
              description: key.description ?? null
            }))
          );
        },
        error: () => {
          this.keyDerivations.set([]);
        }
      });
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    // Key Identifiers are entered and in valid range
    if (
      dataService.isNullOrEmpty(values.otaKeys.scp80.klc.keyIdentifier) ||
      dataService.isNullOrEmpty(values.otaKeys.scp80.kid.keyIdentifier) ||
      dataService.isNullOrEmpty(values.otaKeys.scp80.dek.keyIdentifier) ||
      (+values.adminAgent.configureAdminAgent === 1 && dataService.isNullOrEmpty(values.otaKeys.scp81.psk.keyIdentifier)) ||
      (+values.adminAgent.configureAdminAgent === 1 && dataService.isNullOrEmpty(values.otaKeys.scp81.dek.keyIdentifier))
    ) {
      this.errorMessage = $localize`Key Identifier values must be entered`;
      this.errorFound = true;
    } else if (
      !dataService.validHexValue(values.otaKeys.scp80.klc.keyIdentifier, 1, 1) ||
      !dataService.validHexValue(values.otaKeys.scp80.kid.keyIdentifier, 1, 1) ||
      !dataService.validHexValue(values.otaKeys.scp80.dek.keyIdentifier, 1, 1) ||
      (+values.adminAgent.configureAdminAgent === 1 && !dataService.validHexValue(values.otaKeys.scp81.psk.keyIdentifier, 1, 1)) ||
      (+values.adminAgent.configureAdminAgent === 1 && !dataService.validHexValue(values.otaKeys.scp81.dek.keyIdentifier, 1, 1))
    ) {
      this.errorMessage = $localize`Key Identifier values must be 1 HEX byte in length`;
      this.errorFound = true;
    } else if (
      !dataService.hexValueInRange(values.otaKeys.scp80.klc.keyIdentifier, 0x00, 0xff) ||
      !dataService.hexValueInRange(values.otaKeys.scp80.kid.keyIdentifier, 0x00, 0xff) ||
      !dataService.hexValueInRange(values.otaKeys.scp80.dek.keyIdentifier, 0x00, 0xff) ||
      (+values.adminAgent.configureAdminAgent === 1 && !dataService.hexValueInRange(values.otaKeys.scp81.psk.keyIdentifier, 0x00, 0xff)) ||
      (+values.adminAgent.configureAdminAgent === 1 && !dataService.hexValueInRange(values.otaKeys.scp81.dek.keyIdentifier, 0x00, 0xff))
    ) {
      this.errorMessage = $localize`Key Identifier values must be between 00 and FF`;
      this.errorFound = true;
    }

    // Key Versions are entered and in valid range
    if (
      dataService.isNullOrEmpty(values.otaKeys.scp80.keyVersionNumber) ||
      (+values.adminAgent.configureAdminAgent === 1 && dataService.isNullOrEmpty(values.otaKeys.scp81.keyVersionNumber))
    ) {
      this.errorMessage = $localize`Key Version Number values must be entered`;
      this.errorFound = true;
    } else if (
      !dataService.validHexValue(values.otaKeys.scp80.keyVersionNumber, 1, 1) ||
      (+values.adminAgent.configureAdminAgent === 1 && !dataService.validHexValue(values.otaKeys.scp81.keyVersionNumber, 1, 1))
    ) {
      this.errorMessage = $localize`Key Version Number values must be 1 HEX byte in length`;
      this.errorFound = true;
    } else if (!dataService.hexValueInRange(values.otaKeys.scp80.keyVersionNumber, 0x01, 0x0f)) {
      this.errorMessage = $localize`SCP80 Key Version Number value must be between 01 and 0F`;
      this.errorFound = true;
    } else if (
      +values.adminAgent.configureAdminAgent === 1 &&
      !dataService.hexValueInRange(values.otaKeys.scp81.keyVersionNumber, 0x40, 0x4f)
    ) {
      this.errorMessage = $localize`SCP81 Key Version Number value must be between 40 and 4F`;
      this.errorFound = true;
    } else if (+values.adminAgent.configureAdminAgent === 1 && +values.otaKeys.scp81.keyVersionNumber !== +values.adminAgent.keyVersion) {
      this.errorMessage = $localize`SCP81 Key Version Number value must match the value specified in the Admin Agent section`;
      this.errorFound = true;
    }

    // No Key Data 24 bits are entered for DEK protocols
    if (
      (+values.otaKeys.scp80.dek.keyType === 2 && +values.otaKeys.scp80.dek.keyDataSize === 2 && !values.otaKeys.scp80.dek.isDerived) ||
      (+values.adminAgent.configureAdminAgent === 1 &&
        +values.otaKeys.scp81.dek.keyType === 2 &&
        +values.otaKeys.scp81.dek.keyDataSize === 2 &&
        !values.otaKeys.scp81.dek.isDerived)
    ) {
      this.errorMessage = $localize`Key Data size cannot be 24 bytes when Key Type '88 (AES)' is selected`;
      this.errorFound = true;
    }

    // Key Data Values are entered and in valid range
    if (
      (+values.otaKeys.scp80.klc.keyDataValueGeneration === 2 &&
        !values.otaKeys.scp80.klc.isDerived &&
        !dataService.isNullOrEmpty(values.otaKeys.scp80.klc.keyDataValue) &&
        !dataService.validHexValue(values.otaKeys.scp80.klc.keyDataValue, this.getKeyDataValueSize("scp80.klc", values))) ||
      (+values.otaKeys.scp80.kid.keyDataValueGeneration === 2 &&
        !values.otaKeys.scp80.kid.isDerived &&
        !dataService.isNullOrEmpty(values.otaKeys.scp80.kid.keyDataValue) &&
        !dataService.validHexValue(values.otaKeys.scp80.kid.keyDataValue, this.getKeyDataValueSize("scp80.kid", values))) ||
      (+values.otaKeys.scp80.dek.keyDataValueGeneration === 2 &&
        !values.otaKeys.scp80.dek.isDerived &&
        !dataService.isNullOrEmpty(values.otaKeys.scp80.dek.keyDataValue) &&
        !dataService.validHexValue(values.otaKeys.scp80.dek.keyDataValue, this.getKeyDataValueSize("scp80.dek", values))) ||
      (+values.otaKeys.scp81.psk.keyDataValueGeneration === 2 &&
        +values.adminAgent.configureAdminAgent === 1 &&
        !values.otaKeys.scp81.psk.isDerived &&
        !dataService.isNullOrEmpty(values.otaKeys.scp81.psk.keyDataValue) &&
        !dataService.validHexValue(values.otaKeys.scp81.psk.keyDataValue, this.getKeyDataValueSize("scp81.psk", values))) ||
      (+values.otaKeys.scp81.dek.keyDataValueGeneration === 2 &&
        +values.adminAgent.configureAdminAgent === 1 &&
        !values.otaKeys.scp81.dek.isDerived &&
        !dataService.isNullOrEmpty(values.otaKeys.scp81.dek.keyDataValue) &&
        !dataService.validHexValue(values.otaKeys.scp81.dek.keyDataValue, this.getKeyDataValueSize("scp81.dek", values)))
    ) {
      this.errorMessage = $localize`Key Data Values lengths must equal their corresponding Key Data size`;
      this.errorFound = true;
    }

    return this.errorFound;
  }

  progressCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    const accepted = [2, 3];
    if (
      accepted.includes(+values.otaKeys.scp80.klc.keyDataValueGeneration) &&
      dataService.isNullOrEmpty(values.otaKeys.scp80.klc.keyDataValue)
    ) {
      return false;
    }
    if (
      accepted.includes(+values.otaKeys.scp80.kid.keyDataValueGeneration) &&
      dataService.isNullOrEmpty(values.otaKeys.scp80.kid.keyDataValue)
    ) {
      return false;
    }
    if (
      accepted.includes(+values.otaKeys.scp80.dek.keyDataValueGeneration) &&
      dataService.isNullOrEmpty(values.otaKeys.scp80.dek.keyDataValue)
    ) {
      return false;
    }
    if (
      accepted.includes(+values.otaKeys.scp81.psk.keyDataValueGeneration) &&
      dataService.isNullOrEmpty(values.otaKeys.scp81.psk.keyDataValue)
    ) {
      return false;
    }
    if (
      accepted.includes(+values.otaKeys.scp81.dek.keyDataValueGeneration) &&
      dataService.isNullOrEmpty(values.otaKeys.scp81.dek.keyDataValue)
    ) {
      return false;
    }

    return true;
  }

  getKeyDataValueSize(keyDataValuePointer, values: ProfileModel.ProfileDto) {
    let valueSize = 16;
    let keyDataSizePointer = null;

    if (keyDataValuePointer === "scp80.klc") {
      keyDataSizePointer = values.otaKeys.scp80.klc.keyDataSize;
    } else if (keyDataValuePointer === "scp80.kid") {
      keyDataSizePointer = values.otaKeys.scp80.kid.keyDataSize;
    } else if (keyDataValuePointer === "scp80.dek") {
      keyDataSizePointer = values.otaKeys.scp80.dek.keyDataSize;
    } else if (keyDataValuePointer === "scp81.psk") {
      keyDataSizePointer = values.otaKeys.scp81.psk.keyDataSize;
    } else if (keyDataValuePointer === "scp81.dek") {
      keyDataSizePointer = values.otaKeys.scp81.dek.keyDataSize;
    }

    if (keyDataSizePointer) {
      if (+keyDataSizePointer === 1) {
        valueSize = 16;
      } else if (+keyDataSizePointer === 2) {
        valueSize = 24;
      } else if (+keyDataSizePointer === 3) {
        valueSize = 32;
      }
    }

    return valueSize;
  }

  handleKeyDataValue() {
    setTimeout(() => {
      if (this.extractRawHexString(this.profileValues.otaKeys.scp80.klc.keyDataValue) === "") {
        this.profileValues.otaKeys.scp80.klc.keyDataValue = null;
      }
      if (this.extractRawHexString(this.profileValues.otaKeys.scp80.kid.keyDataValue) === "") {
        this.profileValues.otaKeys.scp80.kid.keyDataValue = null;
      }
      if (this.extractRawHexString(this.profileValues.otaKeys.scp80.dek.keyDataValue) === "") {
        this.profileValues.otaKeys.scp80.dek.keyDataValue = null;
      }
      if (this.extractRawHexString(this.profileValues.otaKeys.scp81.psk.keyDataValue) === "") {
        this.profileValues.otaKeys.scp81.psk.keyDataValue = null;
      }
      if (this.extractRawHexString(this.profileValues.otaKeys.scp81.dek.keyDataValue) === "") {
        this.profileValues.otaKeys.scp81.dek.keyDataValue = null;
      }

      this.handleFormEvents();
    });
  }

  extractRawHexString(hexString) {
    if (hexString) {
      let rawHexString = hexString.replace(/0x/g, "");
      rawHexString = rawHexString.replace(/_/g, "");
      return rawHexString.trim();
    } else {
      return "";
    }
  }

  derivedKeyPlaceholder(protocol: string, key: string) {
    const curr: string = this.profileValues.otaKeys[protocol][key].keyDataValue || null;
    const placeholder =
      protocol === "scp80"
        ? this.scp80KeyOptions().find((option) => curr?.includes(option.label))?.label
        : protocol === "scp81"
          ? this.scp81KeyOptions().find((option) => curr?.includes(option.label))?.label
          : null;
    return placeholder || "Please Select: ";
  }

  enrichKeySelection(selection: KeyDerivationEntity, protocol: string, key: string) {
    const templateKey = this.profileValues.otaKeys[protocol][key];
    templateKey.keyType = key !== "psk" ? `${selection.keyRef.algo}` : "3";
    switch (selection.keyRef.size) {
      case 128:
        templateKey.keyDataSize = "1";
        break;
      case 192:
        templateKey.keyDataSize = "2";
        break;
      case 256:
        templateKey.keyDataSize = "3";
        break;
      default:
        templateKey.keyDataSize = "1";
        break;
    }
    templateKey.keyDataValue = `{{ derive_key('${selection.label}') }}`;
    this.profileValues.otaKeys[protocol][key] = { ...templateKey };
    this.handleFormEvents();
  }

  getKeyDataValueGenerationDisplay(protocol: string, key: string): string {
    const keyConfig = this.profileValues.otaKeys[protocol][key];
    if (keyConfig.isDerived && keyConfig.keyDataValueGeneration === "2") {
      return "3"; // Visual value for "Derived" option
    }
    return keyConfig.keyDataValueGeneration;
  }

  getKeyDataSizeDisplay(protocol: string, key: string): string | null {
    const keyConfig = this.profileValues.otaKeys[protocol][key];
    return keyConfig.isDerived ? null : keyConfig.keyDataSize;
  }

  setKeyDataSize(protocol: string, key: string, value: string) {
    const keyConfig = this.profileValues.otaKeys[protocol][key];
    if (!keyConfig.isDerived) {
      keyConfig.keyDataSize = value;
      this.handleFormEvents();
    }
  }

  setKeyDataValueGeneration(protocol: string, key: string, value: string) {
    const keyConfig = this.profileValues.otaKeys[protocol][key];
    const wasDerived = keyConfig.isDerived;

    if (value === "3") {
      // Derived selected
      keyConfig.keyDataValueGeneration = "2";
      keyConfig.isDerived = true;
    } else if (value === "2") {
      // User Defined selected
      keyConfig.keyDataValueGeneration = "2";
      keyConfig.isDerived = false;
      keyConfig.keyDataValue = null;
      // Reset keyDataSize to default when switching from Derived
      if (wasDerived) {
        keyConfig.keyDataSize = "1";
      }
    } else {
      // eDGAR Generated (value === "1")
      keyConfig.keyDataValueGeneration = value;
      keyConfig.isDerived = false;
      keyConfig.keyDataValue = null;
      // Reset keyDataSize to default when switching from Derived
      if (wasDerived) {
        keyConfig.keyDataSize = "1";
      }
    }

    this.handleFormEvents();
  }
}
